import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Check, Copy, Info, RefreshCw, XCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import AdSlot from '../components/AdSlot';
import { translations } from '../data/translations';
import { getGenerators } from '../data/generators';

export default function Tool({ lang }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const t = (key) => translations[lang][key] || key;

    // Memoize generators to prevent recreation on every render
    const generators = useMemo(() => getGenerators(lang), [lang]);

    // Find generator by path (slug)
    const activeGenerator = useMemo(() => generators.find(g => g.path === slug), [generators, slug]);

    const [inputValue, setInputValue] = useState('');
    const [result, setResult] = useState('');
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('generate');
    const [validationInput, setValidationInput] = useState('');
    const [validationResult, setValidationResult] = useState(null);

    // Initialize default input when generator changes
    useEffect(() => {
        if (activeGenerator) {
            setInputValue(activeGenerator.defaultInput || '');
            setResult(activeGenerator.generate(activeGenerator.defaultInput || ''));
        }
    }, [activeGenerator]);

    useEffect(() => {
        if (!activeGenerator) {
            navigate(`/${lang}`, { replace: true });
            return;
        }
        window.scrollTo(0, 0);
    }, [activeGenerator, navigate, lang]);

    const handleGenerate = () => {
        if (!activeGenerator) return;
        setResult(activeGenerator.generate(inputValue));
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(result);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const highlightJson = (json) => {
        if (!json) return '';
        try {
            // Check if it's already a string or object
            let str = typeof json !== 'string' ? JSON.stringify(json, null, 4) : json;

            // Basic escape
            str = str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

            // Regex for JSON parts
            return str.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
                let cls = 'json-number';
                if (/^"/.test(match)) {
                    if (/:$/.test(match)) {
                        cls = 'json-key';
                    } else {
                        cls = 'json-string';
                    }
                } else if (/true|false/.test(match)) {
                    cls = 'json-boolean';
                } else if (/null/.test(match)) {
                    cls = 'json-null';
                }
                return `<span class="${cls}">${match}</span>`;
            });
        } catch (e) { return json; }
    };

    if (!activeGenerator) return null;

    return (
        <>
            <Helmet>
                <title>{activeGenerator.name} - EveryTool.pro</title>
                <meta name="description" content={`${activeGenerator.name} - ${activeGenerator.description} Free online tool.`} />
                <link rel="canonical" href={`https://everytool.pro/${lang}/${activeGenerator.path}`} />
            </Helmet>

            <AdSlot label={t('adPlaceholder')} />

            <div className="max-w-4xl mx-auto animate-in slide-in-from-bottom-4">
                <div className="bg-white rounded-[3rem] border border-slate-200 shadow-xl overflow-hidden">
                    <div className="p-8 md:p-12 border-b bg-slate-50/50">
                        <div className="flex items-center gap-5 mb-10">
                            <div className="p-4 bg-blue-600 text-white rounded-2xl shadow-xl">{activeGenerator.icon}</div>
                            <div>
                                <h1 className="text-3xl font-black text-slate-900">{activeGenerator.name}</h1>
                                <p className="text-slate-500 font-semibold">{activeGenerator.description}</p>
                            </div>
                        </div>

                        {/* SPECIAL UI FOR BASE64 */}
                        {activeGenerator.id === 'base64' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                <div className="space-y-4">
                                    <label className="block text-sm font-bold text-slate-500 uppercase tracking-widest pl-4">
                                        {lang === 'tr' ? 'Şifrele (Encode)' : 'Encode'}
                                    </label>
                                    <textarea
                                        value={inputValue} // This will be used as encode input
                                        onChange={(e) => {
                                            setInputValue(e.target.value);
                                            try { setResult(btoa(e.target.value)); } catch (err) { setResult('Error'); }
                                        }}
                                        className="w-full p-6 rounded-3xl bg-white border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all font-mono text-slate-700 min-h-[150px]"
                                        placeholder="Text to Base64..."
                                    />
                                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 break-all font-mono text-xs text-slate-600">
                                        {result}
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <label className="block text-sm font-bold text-slate-500 uppercase tracking-widest pl-4">
                                        {lang === 'tr' ? 'Çöz (Decode)' : 'Decode'}
                                    </label>
                                    <textarea
                                        onChange={(e) => {
                                            try {
                                                // We don't save decode input to main state to avoid conflict, just calc locally or use a temp state?
                                                // Simplest: direct render
                                                const decoded = atob(e.target.value);
                                                // We can't easily show it in 'result' variable without overriding encode.
                                                // So we render it locally in a helper or just a div.
                                                // Let's use a data attribute for the output div below.
                                                document.getElementById('decode-output').innerText = decoded;
                                            } catch (err) {
                                                document.getElementById('decode-output').innerText = '(Invalid Base64)';
                                            }
                                        }}
                                        className="w-full p-6 rounded-3xl bg-white border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all font-mono text-slate-700 min-h-[150px]"
                                        placeholder="Base64 to Text..."
                                    />
                                    <div id="decode-output" className="bg-slate-50 p-4 rounded-2xl border border-slate-100 break-all font-mono text-xs text-slate-600">
                                        ...
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* STANDARD INPUT SECTION */
                            <>
                                {/* MODE SWITCHER (If validator exists) */}
                                {activeGenerator.validate && (
                                    <div className="flex gap-2 mb-8 bg-slate-100 p-1.5 rounded-2xl w-fit mx-auto">
                                        <button
                                            onClick={() => setActiveTab('generate')}
                                            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'generate' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            {t('generate')}
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('validate')}
                                            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'validate' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            {lang === 'tr' ? 'Doğrula' : 'Validate'}
                                        </button>
                                    </div>
                                )}

                                {activeTab === 'validate' ? (
                                    /* VALIDATION UI */
                                    <div className="animate-in fade-in zoom-in-95 duration-200">
                                        <div className="mb-8 space-y-3">
                                            <label className="block text-sm font-bold text-slate-500 uppercase tracking-widest pl-4">
                                                {lang === 'tr' ? 'Doğrulanacak Değer' : 'Value to Validate'}
                                            </label>
                                            <input
                                                type="text"
                                                value={validationInput}
                                                onChange={(e) => {
                                                    setValidationInput(e.target.value);
                                                    setValidationResult(activeGenerator.validate(e.target.value));
                                                }}
                                                className="w-full p-6 rounded-3xl bg-white border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all font-bold text-slate-700 font-mono"
                                                placeholder="Paste here..."
                                            />
                                        </div>

                                        {validationInput && validationResult && (
                                            <div className={`w-full rounded-[2rem] p-8 text-center border-2 ${validationResult.isValid ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                                                <div className="flex flex-col items-center gap-3">
                                                    {validationResult.isValid ? <Check size={48} /> : <XCircle size={48} />}
                                                    <h3 className="text-xl font-black">{validationResult.isValid ? (lang === 'tr' ? 'GEÇERLİ' : 'VALID') : (lang === 'tr' ? 'GEÇERSİZ' : 'INVALID')}</h3>
                                                    <p className="font-medium opacity-80">{validationResult.msg}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* GENERATION UI */
                                    <>
                                        {/* RESULT SECTION (Hidden for Base64 as it has custom UI) */}
                                        {activeGenerator.id !== 'base64' && (
                                            <>
                                                {activeGenerator.requiresInput && (
                                                    <div className="mb-8 space-y-3">
                                                        <label className="block text-sm font-bold text-slate-500 uppercase tracking-widest pl-4">
                                                            {activeGenerator.inputLabel}
                                                        </label>
                                                        {activeGenerator.inputType === 'textarea' ? (
                                                            <textarea
                                                                value={inputValue}
                                                                onChange={(e) => {
                                                                    setInputValue(e.target.value);
                                                                    // Real-time update for text transformers
                                                                    if (activeGenerator.id !== 'qr-code') {
                                                                        setResult(activeGenerator.generate(e.target.value));
                                                                    }
                                                                }}
                                                                className="w-full p-6 rounded-3xl bg-white border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all font-mono text-slate-700 min-h-[120px] resize-y"
                                                                placeholder="Type here..."
                                                            />
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                value={inputValue}
                                                                onChange={(e) => {
                                                                    setInputValue(e.target.value);
                                                                    if (activeGenerator.id !== 'qr-code') {
                                                                        setResult(activeGenerator.generate(e.target.value));
                                                                    }
                                                                }}
                                                                className="w-full p-6 rounded-3xl bg-white border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all font-bold text-slate-700"
                                                                placeholder="Type here..."
                                                            />
                                                        )}
                                                    </div>
                                                )}

                                                <div className="relative group">
                                                    {activeGenerator.outputType === 'image' ? (
                                                        <div className="w-full bg-slate-900 border-2 border-slate-800 rounded-[2rem] p-10 flex flex-col items-center justify-center min-h-[220px] shadow-inner">
                                                            <img src={result} alt="Result" className="w-48 h-48 mb-4 shadow-2xl rounded-xl bg-white p-2" />
                                                            <p className="text-xs text-slate-500 font-medium">Right click to save image</p>
                                                        </div>
                                                    ) : activeGenerator.id === 'color-palette' && result ? (
                                                        <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-4 min-h-[160px]">
                                                            {result.split(', ').map((hex, idx) => (
                                                                <div key={idx} className="group relative flex flex-col items-center justify-end p-6 rounded-[2rem] shadow-xl transition-all hover:scale-[1.02] border-4 border-white" style={{ backgroundColor: hex }}>
                                                                    <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-2xl text-[10px] font-black shadow-lg text-slate-900 font-mono tracking-tighter">
                                                                        {hex}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : activeGenerator.outputFormat === 'json' && result && !result.startsWith('Error') && !result.startsWith('Failed') ? (
                                                        <div className="w-full bg-slate-900 border-2 border-slate-800 rounded-[2rem] p-8 md:p-10 text-left text-sm md:text-base font-mono shadow-inner min-h-[160px] overflow-auto text-slate-300">
                                                            <pre className="whitespace-pre-wrap break-all leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightJson(result) }} />
                                                        </div>
                                                    ) : (
                                                        <div className="w-full bg-slate-900 border-2 border-slate-800 rounded-[2rem] p-10 text-center text-lg md:text-xl font-mono text-blue-300 font-bold shadow-inner flex items-center justify-center min-h-[160px] whitespace-pre-wrap overflow-auto">
                                                            {result || <span className="text-slate-700">Waiting for input...</span>}
                                                        </div>
                                                    )}

                                                    {activeGenerator.outputType !== 'image' && result && (
                                                        <button onClick={copyToClipboard} className="absolute top-4 right-4 p-3 bg-slate-800 hover:bg-white hover:text-blue-600 text-slate-400 rounded-2xl shadow-lg flex items-center gap-2 text-xs font-black uppercase active:scale-90 transition-all">
                                                            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? t('copied') : t('copy')}
                                                        </button>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={handleGenerate}
                                                    className="w-full mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black py-6 rounded-[2rem] shadow-xl shadow-blue-200 hover:shadow-2xl hover:shadow-blue-300 active:scale-[0.99] transition-all uppercase tracking-widest flex items-center justify-center gap-3">
                                                    <RefreshCw size={20} className={activeGenerator.requiresInput ? "" : "animate-[spin_1s_ease-in-out]"} /> {t('generate')}
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <Link to={`/${lang}`} className="px-8 py-3 bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-500 rounded-full font-black text-xs uppercase mx-auto w-fit block mt-8 tracking-widest transition-all shadow-sm">← {t('home')}</Link>

                {/* SEO INFORMATION SECTION */}
                {activeGenerator.seoContent && (
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm transition-all hover:shadow-md">
                            <h3 className="text-xl font-black mb-4 flex items-center gap-3 text-slate-900">
                                <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center text-sm">?</div>
                                {lang === 'tr' ? 'Nedir?' : 'What is it?'}
                            </h3>
                            <p className="text-slate-500 font-medium leading-relaxed">{activeGenerator.seoContent[lang]?.details}</p>
                        </div>
                        <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm transition-all hover:shadow-md">
                            <h3 className="text-xl font-black mb-4 flex items-center gap-3 text-slate-900">
                                <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center text-sm">⚙️</div>
                                {lang === 'tr' ? 'Nasıl Çalışır?' : 'How it works?'}
                            </h3>
                            <p className="text-slate-500 font-medium leading-relaxed">{activeGenerator.seoContent[lang]?.howItWorks}</p>
                        </div>
                        <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm transition-all hover:shadow-md">
                            <h3 className="text-xl font-black mb-4 flex items-center gap-3 text-slate-900">
                                <div className="w-8 h-8 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center text-sm">🚀</div>
                                {lang === 'tr' ? 'Kullanım Alanları' : 'Use Cases'}
                            </h3>
                            <p className="text-slate-500 font-medium leading-relaxed">{activeGenerator.seoContent[lang]?.useCases}</p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
